/*
 * @Author: luting
 * @Date: 2022-05-07 13:53:43
 * @lastEditors: luting
 * @LastEditTime: 2023-06-19 14:53:22
 */
import React, { forwardRef, useImperativeHandle, useEffect, useState } from 'react';
import { FormComponentProps } from 'sup-ui/lib/form';
import { Form, Select, DatePicker, Input, Spin } from 'sup-ui';
import { useIntl } from 'react-intl';
import messages from './messages';
import { timeOptions } from './data';
import moment from 'moment';
import 'moment-timezone';

import { getAllTimeZoneApi } from 'root/services/netWork';
import { IPlatformTimeType } from './types';

import Modal from 'root/components/CommonModal';
import Icon from 'root/components/Icon';

import styles from './index.less';

interface IEditSysTime extends FormComponentProps {
  visible: boolean; // 控制modal显示隐藏
  onOk: any; // 外部传入的ok方法
  onCancel: any; // 取消方法
  record: IPlatformTimeType;
  limitPastTime?: boolean; // 限制选择过去的时间
  setFormList: any;
  formList: number[]; // 控制ntp的个数
  init: boolean; // 数否是初次加载页面
  spinning: boolean;
}

/*
record 的格式:
timezone, //时区
isAuto: auto ? 1 : 0, //根据auto为true则为1(自动的), false为0(手动的)
systemTime, //手动的为systemTime
ntpIP, //自动的value为ntp
*/

const { Option } = Select;

const formItemLayout = {
  labelCol: {
    span: 4,
  },
  wrapperCol: {
    span: 20,
  },
};

const EditSysTime = forwardRef<FormComponentProps, IEditSysTime>((props, ref) => {
  const { visible, onOk, onCancel, form, record, limitPastTime, setFormList, formList, init, spinning } = props;
  const { getFieldDecorator } = form;
  const intl = useIntl();
  const [timeZone, setTimeZone] = useState<string[]>([]);
  const [selecTimeZone, setSelecTimeZone] = useState('');

  useImperativeHandle(ref, () => ({
    form,
  }));

  useEffect(() => {
    setSelecTimeZone(record.timezone);
  }, [record]);

  useEffect(() => {
    getAllTimeZoneApi().then((res) => {
      const { data = [] } = res;
      setTimeZone(data);
    });
  }, []);

  const add = () => {
    // 每次增加都从list的最后一个+1
    const id = formList[formList.length - 1] + 1;

    setFormList([...formList, id]);
  };

  const remove = (id: number) => {
    const tempArr = formList?.filter((item) => item !== id);

    // 将record中相对应的数据更改
    if (record.ntpIP) record.ntpIP[id] = undefined;

    setFormList(tempArr);
  };

  const range = (start: any, end: any) => {
    const result = [];
    for (let i = start; i < end; i++) {
      result.push(i);
    }
    return result;
  };
  const disabledStartDate = (current: any) => {
    return current && current < moment(record.systemTime).tz(selecTimeZone);
  };
  const disabledTime = (current: any) => {
    if (new Date(current) > new Date(moment(record.systemTime) as any)) return;
    if (moment(current._d).isSame(record.systemTime, 'd')) {
      const date = new Date(record.systemTime);
      const h = date.getHours();
      const m = date.getMinutes();
      const s = date.getSeconds();

      return {
        disabledHours: () => range(0, h),
        disabledMinutes: () => range(0, m),
        disabledSeconds: () => range(0, s),
      };
    }
  };
  // 切换时区
  const dataChange = (value: string) => {
    setSelecTimeZone(value);
  };

  return (
    <Modal
      className={styles.editSysTimeBox}
      bodyStyle={{ maxHeight: 500, overflowY: 'auto', paddingBottom: 0 }}
      title={intl.formatMessage(init ? messages.confirmSysTime : messages.title)}
      visible={visible}
      onOk={onOk}
      height="auto"
      onCancel={onCancel}
      okButtonProps={{ disabled: spinning }}
      destroyOnClose
    >
      <Spin tip={intl.formatMessage(messages.synchronizing)} spinning={spinning}>
        <Form {...formItemLayout}>
          <Form.Item label={intl.formatMessage(messages.timeZone)}>
            {getFieldDecorator('timezone', {
              initialValue: selecTimeZone,
              rules: [
                {
                  required: true,
                  message: intl.formatMessage(messages.placeholder) + intl.formatMessage(messages.timeZone),
                },
              ],
            })(
              <Select showSearch={true} onChange={dataChange}>
                {timeZone.map((item) => (
                  <Option key={item} value={item}>
                    {item}
                  </Option>
                ))}
              </Select>,
            )}
          </Form.Item>

          <Form.Item className={styles.addStar} label={intl.formatMessage(messages.setTime)} colon={false}>
            <Form.Item>
              {getFieldDecorator('isAuto', {
                initialValue: record?.isAuto,
                rules: [
                  {
                    required: true,
                    message: intl.formatMessage(messages.placeholder) + intl.formatMessage(messages.setTime),
                  },
                ],
              })(
                <Select>
                  {timeOptions.map((item) => (
                    <Option key={item.id} value={item.value}>
                      {item.label.defaultMessage}
                    </Option>
                  ))}
                </Select>,
              )}
            </Form.Item>
            {/* 手动的 */}
            {form.getFieldValue('isAuto') === 0 && (
              <Form.Item>
                {getFieldDecorator('date', {
                  initialValue: record?.systemTime ? moment(record?.systemTime).tz(selecTimeZone) : undefined,
                  rules: [
                    {
                      required: true,
                      message: intl.formatMessage(messages.placeholder) + intl.formatMessage(messages.setTime),
                    },
                  ],
                })(
                  <DatePicker
                    disabledDate={limitPastTime ? disabledStartDate : null}
                    disabledTime={limitPastTime ? disabledTime : null}
                    style={{ width: '100%' }}
                    format="YYYY[年]M[月]D[日] HH:mm"
                    showTime
                    placeholder={intl.formatMessage(messages.placeholder) + intl.formatMessage(messages.setTime)}
                  />,
                )}
                {limitPastTime ? <span className={styles.tip}>{intl.formatMessage(messages.tip)}</span> : null}
              </Form.Item>
            )}
            {/* 自动的 */}
            {form.getFieldValue('isAuto') === 1 &&
              formList.map((item, index) => (
                <Form.Item key={item}>
                  {getFieldDecorator(`ntp[${item}]`, {
                    initialValue: record.ntpIP ? record?.ntpIP[item] : undefined,
                    rules: [
                      {
                        required: !index, // 第0项必填
                        message: intl.formatMessage(messages.placeholderInput) + intl.formatMessage(messages.NTP),
                      },
                      // {
                      //   pattern: new RegExp(
                      //     /^((25[0-5]|2[0-4]\d|((1\d{2})|([1-9]?\d)))\.){3}(25[0-5]|2[0-4]\d|((1\d{2})|([1-9]?\d)))$/,
                      //   ),
                      //   message: intl.formatMessage(messages.rules),
                      // },
                    ],
                  })(
                    <Input
                      style={{ width: formList.length > 1 ? '93%' : '100%' }}
                      placeholder={intl.formatMessage(messages.placeholderInput) + intl.formatMessage(messages.NTP)}
                      maxLength="64"
                    />,
                  )}
                  {formList.length > 1 ? (
                    <Icon onClick={() => remove(item)} style={{ paddingLeft: 6, fontSize: 22 }} type="elete16" />
                  ) : null}
                </Form.Item>
              ))}
            {form.getFieldValue('isAuto') === 1 && (
              <div style={{ width: formList.length > 1 ? '93%' : '100%', textAlign: 'center' }}>
                <Icon onClick={add} style={{ fontSize: 16 }} type="plus16" />
              </div>
            )}
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
});

export default Form.create()(EditSysTime);
